/**
 * PrintOrderDiscountActions — duyệt / từ chối khoản GIẢM GIÁ của một đơn in,
 * ngay tại màn Đơn in (/cskh?tab=upsale), cả ở danh sách lẫn màn chi tiết.
 *
 * Vì sao tồn tại: đơn in có `discount > 0` đẻ ra dòng hàng `pending_approval`
 * (printorder/service.go — writeLines), và cửa thu tiền đóng cho tới khi dòng
 * đó được duyệt. Trước đây nút Duyệt CHỈ có ở /orders/{id} tab Phát sinh, nên
 * người bán đứng ở màn Đơn in nhìn thấy "Chờ duyệt giảm giá" mà không làm gì
 * được, còn người duyệt phải mò ra mã đơn rồi mở trang khác. Hai màn, một
 * khoản tiền.
 *
 * Nút chỉ vẽ cho người giữ `order.surcharge.approve` — cùng cổng với máy chủ
 * (route POST /surcharges/{id}/approve|reject). Người khác thấy nguyên câu
 * chỉ dẫn như cũ, không thấy nút rồi ăn 403.
 */

import { toast } from "sonner";
import { Button } from "@/ui/button";
import { useCanApproveSurcharge } from "@/features/orders/surchargePermissions";
import {
  useApprovePrintOrderDiscount,
  useRejectPrintOrderDiscount,
  type CskhPrintOrder,
} from "../api";
import { hasPendingDiscount } from "../printOrderShared";

export function PrintOrderDiscountActions({ po }: { po: CskhPrintOrder }) {
  const canApprove = useCanApproveSurcharge();
  const approveM = useApprovePrintOrderDiscount(po.orderId);
  const rejectM = useRejectPrintOrderDiscount(po.orderId);

  if (!canApprove || !hasPendingDiscount(po)) return null;
  const surchargeId = po.surchargeId!;
  const busy = approveM.isPending || rejectM.isPending;

  const doApprove = async () => {
    try {
      await approveM.mutateAsync(surchargeId);
      toast.success("Đã duyệt giảm giá — đơn in thu tiền được");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Lỗi khi duyệt giảm giá");
    }
  };

  const doReject = async () => {
    // Lý do không bắt buộc (BE nhận rỗng) — bấm Cancel là bỏ hành động, đúng
    // khuôn hộp từ chối ở tab Phát sinh trên màn đơn hàng.
    const typed = window.prompt("Lý do từ chối giảm giá (không bắt buộc):");
    if (typed === null) return;
    const reason = typed.trim();
    try {
      await rejectM.mutateAsync(reason ? { surchargeId, reason } : { surchargeId });
      toast.success("Đã từ chối giảm giá — huỷ đơn in rồi tạo lại không giảm giá");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Lỗi khi từ chối giảm giá");
    }
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={() => void doApprove()}
      >
        Duyệt
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={busy}
        onClick={() => void doReject()}
      >
        Từ chối
      </Button>
    </>
  );
}
